/**
 * DoubleTake (the VLM false-positive filter) per product on the Edit Camera
 * Products tab (ENG-1306).
 *
 * The API contract: every entry in `stream_metrics` carries
 * `vlm_enabled` (read/write; `true` is rejected with a 400 on a product whose
 * label isn't supported) and `vlm_supported` (read-only). The stream-level
 * `vlm_enabled` is derived and never written by this UI.
 */

export const DOUBLE_TAKE_COLUMN_TITLE = "DOUBLETAKE";

export const DOUBLE_TAKE_UNSUPPORTED_TOOLTIP =
  "DoubleTake isn't available for this product yet";

/**
 * The DoubleTake column only exists on a site that has DoubleTake at all:
 * the `showVlmFilters` LD flag (Alerts page badge + filters) AND the
 * site-level master switch (`customer/{id}/about/` `vlm_enabled`).
 */
export const isDoubleTakeVisible = (
  flags: Record<string, unknown> | undefined,
  siteInfo: { vlm_enabled?: boolean } | undefined
): boolean => Boolean(flags?.showVlmFilters) && Boolean(siteInfo?.vlm_enabled);

/**
 * A product gets an enabled DoubleTake switch only when the API row says
 * `vlm_supported: true`. A missing field is treated as NOT supported (fail
 * closed): if camera-ui ever ships ahead of admin we'd rather show disabled
 * switches than let users enable DoubleTake on products the verifier can't
 * handle.
 */
export const isDoubleTakeSupported = (metric: {
  vlm_supported?: boolean;
}): boolean => metric?.vlm_supported === true;

export const DOUBLE_TAKE_INACTIVE_TOOLTIP = "Enable the product first";

/**
 * Why a product's DoubleTake switch is disabled, or `null` when it is
 * editable. Inactive products come first: the metric write contract treats a
 * PATCH without `active: true` as a delete (`MetricSerializer.save` →
 * `delete_metric`), and there is no metric row to carry the flag anyway, so
 * toggling is only possible on an active product.
 */
export const getDoubleTakeDisabledReason = (metric: {
  active?: boolean;
  vlm_supported?: boolean;
}): string | null => {
  if (!metric?.active) {
    return DOUBLE_TAKE_INACTIVE_TOOLTIP;
  }
  if (!isDoubleTakeSupported(metric)) {
    return DOUBLE_TAKE_UNSUPPORTED_TOOLTIP;
  }
  return null;
};

/**
 * True when the staged metric's DoubleTake state differs from the backup.
 * Treats a missing value (settings row that predates the field, or a metric
 * never touched) as "no change" so unrelated saves don't emit `vlm_enabled`.
 */
export const hasDoubleTakeChanged = (
  currentMetric: { vlm_enabled?: boolean },
  previousMetric: { vlm_enabled?: boolean } | null | undefined
): boolean =>
  currentMetric?.vlm_enabled !== undefined &&
  currentMetric.vlm_enabled !== previousMetric?.vlm_enabled;

/**
 * Stage a DoubleTake toggle on one product row of `productsData` (the
 * Products tab's grouped shape: `[{ label_name: <group>, metrics: { <metric
 * label>: MetricSerializer row } }]`). Returns a new array; the save path
 * diffs it against the backup and PATCHes `/metric/{id}/` with `vlm_enabled`.
 */
export const setMetricDoubleTake = (
  productsData: any[],
  groupLabelName: string,
  metricLabelName: string,
  vlmEnabled: boolean
): any[] =>
  productsData.map((product: any) => {
    if (product.label_name !== groupLabelName) {
      return product;
    }
    const metric = product.metrics?.[metricLabelName];
    if (!metric) {
      return product;
    }
    return {
      ...product,
      metrics: {
        ...product.metrics,
        [metricLabelName]: { ...metric, vlm_enabled: vlmEnabled },
      },
    };
  });
